import React, { JSX, useRef } from 'react';
import styles from './Scrubber.module.css';

export interface ScrubberMarker {
  /** 0-based step the marker sits on. */
  step: number;
  /** Styling hook — e.g. 'human' | 'ai' | 'ed' | 'es'. */
  kind: string;
  label: string;
  /** Short visible text drawn above the tick, e.g. 'ED'. */
  text?: string;
}

interface Props {
  /** Number of discrete positions — one per frame of the clip. */
  stepCount: number;
  /** Current 0-based step. */
  value: number;
  onChange: (step: number) => void;
  markers?: ScrubberMarker[];
  /** A marker was clicked. Falls back to onChange when omitted. */
  onMarkerClick?: (step: number) => void;
}

/**
 * Frame scrubber. The track has exactly `stepCount` positions, laid out at
 * step / (stepCount - 1) along it; the thumb, the fill and the markers all use
 * that one ratio, so they align by construction.
 */
export function Scrubber({
  stepCount,
  value,
  onChange,
  markers = [],
  onMarkerClick,
}: Props): JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const lastEmittedRef = useRef<number | null>(null);

  const lastStep = Math.max(stepCount - 1, 0);
  const ratioOf = (step: number) => (lastStep > 0 ? step / lastStep : 0);

  const emit = (step: number) => {
    if (step === lastEmittedRef.current) return;
    lastEmittedRef.current = step;
    onChange(step);
  };

  const stepAt = (clientX: number): number => {
    const rect = trackRef.current!.getBoundingClientRect();
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    return Math.max(0, Math.min(Math.round(ratio * lastStep), lastStep));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    lastEmittedRef.current = null;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    emit(stepAt(e.clientX));
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) emit(stepAt(e.clientX));
  };

  const endDrag = () => {
    draggingRef.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next: number;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = value - 1;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = value + 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = lastStep;
    else return;
    e.preventDefault();
    next = Math.max(0, Math.min(next, lastStep));
    if (next !== value) onChange(next);
  };

  const position = `${ratioOf(value) * 100}%`;

  return (
    <div
      className={styles.scrubber}
      role="slider"
      tabIndex={0}
      aria-label="Frame"
      aria-valuemin={1}
      aria-valuemax={stepCount}
      aria-valuenow={value + 1}
      aria-valuetext={`Frame ${value + 1} of ${stepCount}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.track} ref={trackRef} data-testid="scrubber-track">
        <div className={styles.fill} style={{ width: position }} />
        {markers.map((marker) => (
          <button
            // Markers of different kinds can share a step (AP-909).
            key={`${marker.kind}-${marker.step}`}
            type="button"
            className={styles.marker}
            data-kind={marker.kind}
            style={{ left: `${ratioOf(marker.step) * 100}%` }}
            title={marker.label}
            aria-label={marker.label}
            // A marker press is a jump to exactly that frame, not a scrub:
            // keep it from starting a drag that would resolve to a neighbour.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => (onMarkerClick ?? onChange)(marker.step)}
          >
            {marker.text && (
              <span className={styles.markerText} aria-hidden>
                {marker.text}
              </span>
            )}
          </button>
        ))}
        <div
          className={styles.thumb}
          style={{ left: position }}
          data-testid="scrubber-thumb"
        />
      </div>
    </div>
  );
}
